import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState, useMemo } from 'react';
import { Box, Typography, IconButton, Button, Chip, } from '@mui/material';
import { Edit, Trash2, Plus, Star, ExternalLink } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchSampleTrackerList, manageSampleTracker, setSampleTrackerSearch, setSampleTrackerSort, } from '../../redux/sampleTrackerSlice';
import { useAccountOverview } from '../../context/AccountOverviewContext';
import CommonTable from '../../common_components/CommonTable';
import TableToolbar from '../../common_components/TableToolbar';
import { selectEntityType } from '../../redux/authSlice';
import HoverTooltip from '../../common_components/hoverOverlay/HoverTooltip';
import ConfirmActionModal from '../../common_components/ConfirmActionModal';
import useToast from '../../common_components/Toast/useToast';
import SampleTrackerDrawer from './SampleTrackerDrawer';
// ── helpers ──────────────────────────────────────────────────────────────────
const fmtDate = (val) => {
    if (!val)
        return '—';
    try {
        return new Date(val).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
    }
    catch {
        return val;
    }
};
// ── Rating cell: "3 ★" in orange ─────────────────────────────────────────────
const RatingCell = ({ value }) => {
    if (!value)
        return _jsx(Typography, { sx: { fontSize: '14px !important', color: '#9CA3AF' }, children: "\u2014" });
    return (_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: '4px' }, children: [_jsx(Typography, { sx: { fontSize: '14px !important', fontWeight: 600, color: '#F59E0B' }, children: value }), _jsx(Star, { size: 13, fill: "#F59E0B", stroke: "#F59E0B" })] }));
};
// ── Yes / No badge ────────────────────────────────────────────────────────────
const YesNo = ({ value }) => (_jsx(Typography, { sx: {
        fontSize: '14px !important',
        fontWeight: 600,
        color: value ? '#027A48' : '#B42318',
    }, children: value ? 'Yes' : 'No' }));
// ── Expanded products sub-table ───────────────────────────────────────────────
const PRODUCT_COLS = [
    { key: 'product', label: 'Product', fr: '1.8fr' },
    { key: 'sample_sent_date', label: 'Sample Sent Date', fr: '1.4fr' },
    { key: 'tracking', label: 'Tracking', fr: '1.4fr' },
    { key: 'published_date', label: 'Published Date', fr: '1.4fr' },
    { key: 'rating', label: 'Rating', fr: '0.9fr' },
    { key: 'sample_sent', label: 'Sample Sent', fr: '1fr' },
    { key: 'sample_received', label: 'Sample Received', fr: '1.1fr' },
];
const COLS_TEMPLATE = PRODUCT_COLS.map(c => c.fr).join(' ');
const ProductsTable = ({ products }) => {
    if (products.length === 0) {
        return (_jsx(Box, { sx: { py: 2.5, px: 4, color: '#9CA3AF', fontSize: '14px' }, children: "No products added." }));
    }
    return (_jsxs(Box, { sx: { p: 1 }, children: [_jsx(Box, { sx: {
                    display: 'grid',
                    gridTemplateColumns: COLS_TEMPLATE,
                    px: 1, py: 1, gap: 1,
                    borderBottom: '1px solid #F3F4F6',
                    bgcolor: '#F9FAFB',
                    borderRadius: '8px 8px 0 0',
                    border: '1px solid #F3F4F6',
                    borderBottomLeftRadius: 0,
                    borderBottomRightRadius: 0,
                }, children: PRODUCT_COLS.map(col => (_jsx(Typography, { sx: { fontSize: '12px', fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.04em' }, children: col.label }, col.key))) }), _jsx(Box, { sx: { border: '1px solid #F3F4F6', borderTop: 'none', borderRadius: '0 0 8px 8px', overflow: 'hidden' }, children: products.map((p, i) => (_jsxs(Box, { sx: {
                        display: 'grid',
                        gridTemplateColumns: COLS_TEMPLATE,
                        px: 2, py: 1.5, gap: 1,
                        alignItems: 'center',
                        borderTop: i > 0 ? '1px solid #F3F4F6' : 'none',
                        bgcolor: '#fff',
                        '&:hover': { bgcolor: '#FAFAFA' },
                    }, children: [_jsx(Typography, { sx: { fontSize: '14px !important', color: '#101828', fontWeight: 500 }, children: p.product || '—' }), _jsx(Typography, { sx: { fontSize: '14px !important', color: '#374151' }, children: fmtDate(p.sample_sent_date) }), _jsx(Typography, { sx: { fontSize: '14px !important', color: '#6B7280' }, children: p.tracking || '—' }), _jsx(Typography, { sx: { fontSize: '14px !important', color: '#374151' }, children: fmtDate(p.published_date) }), _jsx(RatingCell, { value: p.rating }), _jsx(YesNo, { value: p.sample_sent }), _jsx(YesNo, { value: p.sample_received })] }, i))) })] }));
};
const SampleTrackerList = ({ clientName = '' }) => {
    const dispatch = useDispatch();
    const toast = useToast();
    const { selectedClientId } = useAccountOverview();
    const { records, loading, search, sortBy, sortOrder } = useSelector((state) => state.sampleTracker);
    const entityType = useSelector(selectEntityType);
    const isClient = entityType === 'client';
    const permissions = useSelector((state) => state.auth.permissions);
    const affiliateActions = permissions?.affiliate_account_performance?.actions || [];
    // Seed data has used both "Simple Tracker" and the UI's "Sample Tracker" spelling for this permission.
    const hasSampleTrackerAccess = affiliateActions.includes('Simple Tracker') || affiliateActions.includes('Sample Tracker');
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [editRecord, setEditRecord] = useState(null);
    const [deleteId, setDeleteId] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [hiddenColumns, setHiddenColumns] = useState(new Set());
    const [columnVisibilityAnchor, setColumnVisibilityAnchor] = useState(null);
    // sortConfig shape that CommonTable expects
    const sortConfig = { columnId: sortBy, direction: sortOrder };
    const loadList = () => {
        dispatch(fetchSampleTrackerList({ clientId: selectedClientId }));
    };
    useEffect(() => {
        loadList();
    }, [selectedClientId]);
    // Re-fetch when sort changes
    useEffect(() => {
        loadList();
    }, [sortBy, sortOrder]);
    const handleSortChange = (columnId, direction) => {
        dispatch(setSampleTrackerSort({
            sortBy: columnId,
            sortOrder: direction === 'asc' ? 'asc' : 'desc',
        }));
    };
    // Client-side search filter (instant; search also goes to backend via loadList when user stops typing)
    const filteredRows = useMemo(() => {
        if (!search.trim())
            return records;
        const q = search.toLowerCase();
        return records.filter(r => r.name.toLowerCase().includes(q) ||
            r.email_address.toLowerCase().includes(q));
    }, [records, search]);
    const handleEdit = (record) => {
        setEditRecord(record);
        setDrawerOpen(true);
    };
    const handleDelete = async () => {
        if (!deleteId)
            return;
        setDeleteLoading(true);
        try {
            await dispatch(manageSampleTracker({ action: 'delete', id: deleteId })).unwrap();
            toast.success('Record deleted.');
            loadList();
        }
        catch (err) {
            toast.error(err || 'Failed to delete.');
        }
        finally {
            setDeleteLoading(false);
            setDeleteId(null);
        }
    };
    const handleDrawerClose = () => {
        setDrawerOpen(false);
        setEditRecord(null);
    };
    // ── Column definitions ────────────────────────────────────────────────────
    const columns = [
        {
            id: 'name',
            label: 'Influencer/Contact Name',
            sortable: true,
            renderCell: (row) => (_jsxs(Box, { children: [_jsx(Typography, { sx: { fontSize: '14px !important', fontWeight: 500, color: '#101828' }, children: row.name }), row.products.length > 0 && (_jsx(Chip, { label: `${row.products.length} product${row.products.length > 1 ? 's' : ''}`, size: "small", sx: { mt: '3px', height: '18px', fontSize: '11px', bgcolor: '#F3F4F6', color: '#6B7280', '.MuiChip-label': { px: 1 } } }))] })),
        },
        {
            id: 'email_address',
            label: 'Email Address',
            sortable: true,
            renderCell: (row) => (_jsx(Typography, { sx: { fontSize: '14px !important', color: '#374151' }, children: row.email_address || '—' })),
        },
        {
            id: 'social_website_link',
            label: 'Social/Website Link',
            sortable: true,
            renderCell: (row) => {
                if (!row.social_website_link) {
                    return _jsx(Typography, { sx: { fontSize: '14px !important', color: '#9CA3AF' }, children: "\u2014" });
                }
                return (_jsx(HoverTooltip, { title: row.social_website_link, placement: "top", children: _jsxs(Box, { component: "a", href: row.social_website_link, target: "_blank", rel: "noopener noreferrer", onClick: (e) => e.stopPropagation(), sx: {
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px',
                            color: '#374151',
                            textDecoration: 'none',
                            fontSize: '14px',
                            maxWidth: '220px',
                            overflow: 'hidden',
                            '&:hover': { color: '#2563EB' },
                        }, children: [_jsx(Typography, { sx: {
                                    fontSize: '14px !important',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    flexShrink: 1,
                                }, children: row.social_website_link }), _jsx(ExternalLink, { size: 13, style: { flexShrink: 0 } })] }) }));
            },
        },
        ...(!isClient && hasSampleTrackerAccess ? [{
                id: 'actions',
                label: 'Action',
                width: 80,
                renderCell: (row) => (_jsxs(Box, { sx: { display: 'flex', gap: '2px' }, onClick: e => e.stopPropagation(), children: [_jsx(HoverTooltip, { title: "Edit", placement: "top", children: _jsx(IconButton, { size: "small", onClick: () => handleEdit(row), sx: { color: '#98A2B3', '&:hover': { color: '#101828', bgcolor: '#F3F4F6' }, borderRadius: '6px' }, children: _jsx(Edit, { size: 15 }) }) }), _jsx(HoverTooltip, { title: "Delete", placement: "top", children: _jsx(IconButton, { size: "small", onClick: () => setDeleteId(row.id), sx: { color: '#98A2B3', '&:hover': { color: '#EF4444', bgcolor: '#FEF2F2' }, borderRadius: '6px' }, children: _jsx(Trash2, { size: 15 }) }) })] })),
            }] : []),
    ];
    return (_jsxs(Box, { className: "table-scroll-container", children: [_jsx(TableToolbar, { showSearch: true, showRows: true, showFullScreen: true, enableColumnVisibility: true, title: "", searchValue: search, onSearchChange: val => dispatch(setSampleTrackerSearch(val)), onColumnVisibilityClick: e => setColumnVisibilityAnchor(e.currentTarget), searchPlaceholder: "Search name or email...", customActions: !isClient && hasSampleTrackerAccess ? (_jsx(Button, { variant: "contained", size: "small", startIcon: _jsx(Plus, { size: 14 }), onClick: () => { setEditRecord(null); setDrawerOpen(true); }, sx: {
                        bgcolor: '#EE2A7B',
                        '&:hover': { bgcolor: '#d42070' },
                        borderRadius: '8px',
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '13px',
                        px: 2,
                        height: '36px',
                        whiteSpace: 'nowrap',
                    }, children: "Add New Sample" })) : undefined }), _jsx(CommonTable, { columns: columns, rows: filteredRows, isLoading: loading, isFirstLoad: loading && records.length === 0, sortConfig: sortConfig, onSortChange: handleSortChange, emptyMessage: search
                    ? 'No records match your search.'
                    : 'No sample tracker records yet. Click "Add New Sample" to get started.', renderExpandedContent: (row) => (_jsx(ProductsTable, { products: row.products })), isRowExpandable: (row) => row.products.length > 0, enableColumnVisibility: true, hiddenColumns: hiddenColumns, onColumnVisibilityChange: setHiddenColumns, columnVisibilityAnchorEl: columnVisibilityAnchor, onColumnVisibilityClose: () => setColumnVisibilityAnchor(null), columnVisibilityConfig: {
                    enable: true,
                    columns: ['name', 'email_address', 'social_website_link', ...(!isClient && hasSampleTrackerAccess ? ['actions'] : [])],
                } }), _jsx(SampleTrackerDrawer, { open: drawerOpen, onClose: handleDrawerClose, record: editRecord, clientId: selectedClientId, onSuccess: loadList }), _jsx(ConfirmActionModal, { open: !!deleteId, onClose: () => setDeleteId(null), onConfirm: handleDelete, title: "Delete Record", description: "Are you sure you want to delete this sample tracker record? This action cannot be undone.", confirmText: "Delete", loading: deleteLoading })] }));
};
export default SampleTrackerList;
